import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  RefreshCw,
  Loader2,
  PlaneTakeoff,
  ChevronDown,
  ChevronRight,
  Users,
  Wallet,
  Activity,
  Calendar,
  Mail,
  Archive,
  SlidersHorizontal,
  Check,
  LayoutDashboard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { setReserveActAsUserId } from '@/lib/reserveActAs';
import { fmtCurrency } from '@/utils/money';

const statusColors = {
  active: 'bg-emerald-500/10 text-emerald-400',
  paused: 'bg-brand-500/10 text-brand-400',
  completed: 'bg-brand-500/10 text-brand-400',
  cancelled: 'bg-red-500/10 text-red-400',
  ticketed: 'bg-brand-500/10 text-brand-400'
};

const frequencyLabels = { weekly: 'Weekly', biweekly: 'Biweekly', monthly: 'Monthly' };

function formatCurrency(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format((cents ?? 0) / 100);
}

function formatDate(iso) {
  if (!iso) return '—';
  return format(parseISO(iso), 'MMM d, yyyy');
}

function formatRoute(trip) {
  const firstSlice = trip.flight_data?.slices?.[0];
  const firstSeg = firstSlice?.segments?.[0];
  const lastSeg = firstSlice?.segments?.[firstSlice?.segments?.length - 1];
  const dep = firstSeg?.origin?.iata_code || trip.departure_city || '?';
  const arr = lastSeg?.destination?.iata_code || trip.arrival_city || '?';
  return `${dep} → ${arr}`;
}

function userPrimaryLabel(userInfo) {
  const name = userInfo?.full_name && String(userInfo.full_name).trim();
  return name || userInfo?.email || 'Unknown';
}

function userShowEmailBelow(userInfo) {
  const name = userInfo?.full_name && String(userInfo.full_name).trim();
  return Boolean(name && userInfo?.email);
}

export default function AdminAllTripsModal({ open, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ trips: [], balances: {}, users: {}, ledger: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Balance adjustment state: keyed by userId
  const [adjustingUser, setAdjustingUser] = useState(null); // userId being adjusted
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustDescription, setAdjustDescription] = useState('');
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);

  function openAdjust(userId) {
    setAdjustingUser(userId);
    setAdjustAmount('');
    setAdjustDescription('');
  }

  function cancelAdjust() {
    setAdjustingUser(null);
    setAdjustAmount('');
    setAdjustDescription('');
  }

  function openCustomerReserveDashboard(userId) {
    if (!userId) return;
    setReserveActAsUserId(String(userId));
    onClose();
    navigate('/app/reserve', { state: { reserveActAsUserId: String(userId) } });
  }

  async function submitAdjust(userId) {
    const dollars = parseFloat(adjustAmount);
    if (isNaN(dollars) || dollars === 0) {
      toast.error('Enter a non-zero amount (use negative to deduct)');
      return;
    }
    if (!adjustDescription.trim()) {
      toast.error('A description is required');
      return;
    }
    setAdjustSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/admin/reserve/adjust-balance', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          amount_cents: Math.round(dollars * 100),
          description: adjustDescription.trim(),
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to adjust balance');

      const newBalance = json.new_balance_cents ?? 0;
      setData((prev) => ({
        ...prev,
        balances: { ...prev.balances, [userId]: newBalance },
      }));
      toast.success(`Balance adjusted. New balance: ${formatCurrency(newBalance)}`);
      cancelAdjust();
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'Failed to adjust balance');
    } finally {
      setAdjustSubmitting(false);
    }
  }

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please sign in');
        return;
      }
      const res = await fetch('/api/admin/reserve/all-trips', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (res.status === 403) toast.error('Admin access required');
        else toast.error(err.error || 'Failed to load');
        return;
      }
      const json = await res.json();
      setData({
        trips: json.trips || [],
        balances: json.balances || {},
        users: json.users || {},
        ledger: json.ledger || []
      });
    } catch (e) {
      toast.error(e.message || 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  // Split trips: active (non-cancelled) vs archived (cancelled)
  const { activeTrips, archivedTrips } = React.useMemo(() => {
    const all = data.trips || [];
    const active = all.filter((t) => t.status !== 'cancelled');
    const archived = all.filter((t) => t.status === 'cancelled');
    return { activeTrips: active, archivedTrips: archived };
  }, [data.trips]);

  // Group active trips by user
  const byUser = React.useMemo(() => {
    const map = {};
    activeTrips.forEach((t) => {
      if (!map[t.user_id]) map[t.user_id] = [];
      map[t.user_id].push(t);
    });
    return map;
  }, [activeTrips]);

  // Group archived trips by user
  const archivedByUser = React.useMemo(() => {
    const map = {};
    archivedTrips.forEach((t) => {
      if (!map[t.user_id]) map[t.user_id] = [];
      map[t.user_id].push(t);
    });
    return map;
  }, [archivedTrips]);

  const userList = Object.keys(byUser);

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex flex-col bg-[#050505]"
      >
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 sm:px-6 border-b border-white/10 bg-[#0a0a0a]/80 backdrop-blur-xl">
            <div className="flex items-center gap-3">
              <h2 className="text-lg sm:text-xl font-light text-white">All Reserve Trips</h2>
              <span className="text-white/40 text-sm font-normal">
                {activeTrips.length} active • {archivedTrips.length} archived • {userList.length} user{userList.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchAll}
                disabled={isLoading}
                className="text-white/50 hover:text-white hover:bg-white/5 rounded-full border border-white/10"
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                <span className="ml-1.5 hidden sm:inline">Refresh</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-white/50 hover:text-white hover:bg-white/5 rounded-full border border-white/10"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 sm:py-6">
            {isLoading && !data.trips?.length ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="w-8 h-8 animate-spin text-white/40" />
              </div>
            ) : !data.trips?.length && !archivedTrips.length ? (
              <div className="text-center py-24">
                <PlaneTakeoff className="w-12 h-12 mx-auto text-white/20 mb-4" />
                <p className="text-white/60 font-normal">No Reserve trips yet</p>
              </div>
            ) : (
              <div className="space-y-6 max-w-4xl mx-auto">
                {/* Summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                    <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Active Trips</p>
                    <p className="text-white text-lg sm:text-xl font-light mt-0.5">{activeTrips.length}</p>
                  </div>
                  <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                    <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Archived</p>
                    <p className="text-white/50 text-lg sm:text-xl font-light mt-0.5">{archivedTrips.length}</p>
                  </div>
                  <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                    <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Total Balance</p>
                    <p className="text-emerald-400 text-lg sm:text-xl font-light mt-0.5">
                      {formatCurrency(Object.values(data.balances || {}).reduce((a, b) => a + (b || 0), 0))}
                    </p>
                  </div>
                  <div className="rounded-xl bg-black/40 border border-white/10 p-3 sm:p-4">
                    <p className="text-white/40 text-xs font-normal uppercase tracking-wider">Users</p>
                    <p className="text-white text-lg sm:text-xl font-light mt-0.5">{userList.length}</p>
                  </div>
                </div>

                {/* Toggle Archived */}
                {archivedTrips.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowArchived((s) => !s)}
                    className="flex items-center gap-2 text-white/60 hover:text-white/80 text-sm"
                  >
                    {showArchived ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <Archive className="w-4 h-4" />
                    Archived ({archivedTrips.length} canceled)
                  </button>
                )}

                {showArchived && archivedTrips.length > 0 && (
                  <div className="rounded-2xl bg-black/40 border border-white/10 overflow-hidden">
                    <h3 className="text-white/70 font-normal px-4 py-3 flex items-center gap-2 border-b border-white/10">
                      <Archive className="w-4 h-4 text-white/50" />
                      Archived (Canceled Reservations)
                    </h3>
                    <div className="p-4 space-y-3">
                      {Object.keys(archivedByUser).map((userId) => {
                        const trips = archivedByUser[userId] || [];
                        const userInfo = data.users[userId] || {};
                        const balance = data.balances[userId] ?? 0;
                        const isAdjusting = adjustingUser === `archived-${userId}`;
                        return (
                          <div key={userId} className="rounded-xl bg-black/30 border border-white/10 overflow-hidden">
                            {/* Archived user header */}
                            <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                              <div className="flex items-start gap-2 min-w-0 flex-1">
                                <Mail className="w-3.5 h-3.5 text-white/30 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <p className="text-white/85 text-xs sm:text-sm font-medium truncate">{userPrimaryLabel(userInfo)}</p>
                                  {userShowEmailBelow(userInfo) ? (
                                    <p className="text-white/40 text-[11px] truncate mt-0.5">{userInfo.email}</p>
                                  ) : null}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="text-right">
                                  <p className={`text-xs font-normal ${balance > 0 ? 'text-emerald-400' : balance < 0 ? 'text-red-400' : 'text-white/30'}`}>
                                    {formatCurrency(balance)}
                                  </p>
                                  <p className="text-white/30 text-[10px] uppercase">Balance</p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => openCustomerReserveDashboard(userId)}
                                  className="p-1.5 rounded-lg text-white/30 hover:text-emerald-400/90 hover:bg-white/5 transition-colors"
                                  title="Open customer Reserve dashboard"
                                >
                                  <LayoutDashboard className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => isAdjusting ? cancelAdjust() : openAdjust(`archived-${userId}`)}
                                  className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors"
                                  title="Adjust balance"
                                >
                                  <SlidersHorizontal className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            {/* Inline adjustment form for archived user */}
                            <AnimatePresence>
                              {isAdjusting && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.15 }}
                                  className="border-t border-white/10 overflow-hidden"
                                >
                                  <div className="px-3 py-3 bg-white/[0.02]">
                                    <p className="text-white/50 text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                      <SlidersHorizontal className="w-3 h-3" />
                                      Adjust Balance
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                      <div className="relative w-full sm:w-32">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          placeholder="0.00"
                                          value={adjustAmount}
                                          onChange={(e) => setAdjustAmount(e.target.value)}
                                          className="pl-6 bg-black/40 border-white/10 text-white placeholder:text-white/20 text-sm h-9"
                                          autoFocus
                                        />
                                      </div>
                                      <Input
                                        type="text"
                                        placeholder="Reason (e.g. Refund processed via Stripe)"
                                        value={adjustDescription}
                                        onChange={(e) => setAdjustDescription(e.target.value)}
                                        className="flex-1 bg-black/40 border-white/10 text-white placeholder:text-white/20 text-sm h-9"
                                        onKeyDown={(e) => e.key === 'Enter' && submitAdjust(userId)}
                                      />
                                      <div className="flex gap-1.5">
                                        <Button
                                          size="sm"
                                          onClick={() => submitAdjust(userId)}
                                          disabled={adjustSubmitting}
                                          className="h-9 px-3 bg-emerald-600 hover:bg-emerald-500 text-white border-0"
                                        >
                                          {adjustSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                          <span className="ml-1">Apply</span>
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={cancelAdjust}
                                          disabled={adjustSubmitting}
                                          className="h-9 px-3 text-white/40 hover:text-white hover:bg-white/5"
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                    <p className="text-white/30 text-[11px] mt-2">
                                      Use a negative amount to deduct (e.g. <span className="text-white/50">-48.62</span> to wipe a refunded payment).
                                    </p>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {/* Archived trips list */}
                            <div className="space-y-2 px-3 pb-3 border-t border-white/5">
                              {trips.map((trip) => (
                                <div
                                  key={trip.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => openCustomerReserveDashboard(userId)}
                                  onKeyDown={(e) => e.key === 'Enter' && openCustomerReserveDashboard(userId)}
                                  className="rounded-lg bg-black/20 border border-white/5 p-3 mt-2 opacity-80 cursor-pointer hover:bg-white/[0.04] hover:border-white/10 transition-colors"
                                  title="Open customer Reserve dashboard"
                                >
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-white/70 font-medium">{formatRoute(trip)}</span>
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-normal bg-red-500/10 text-red-400">
                                          cancelled
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-3 mt-1.5 text-white/40 text-xs">
                                        <span className="flex items-center gap-1">
                                          <Calendar className="w-3 h-3" />
                                          {formatDate(trip.departure_date)}
                                        </span>
                                        <span>{formatDate(trip.updated_at || trip.created_at)}</span>
                                      </div>
                                    </div>
                                    <div className="text-right text-white/40 text-xs">
                                      {formatCurrency(trip.paid_amount_cents)} paid
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Toggle Activity */}
                <button
                  type="button"
                  onClick={() => setShowActivity((s) => !s)}
                  className="flex items-center gap-2 text-white/60 hover:text-white/80 text-sm"
                >
                  {showActivity ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <Activity className="w-4 h-4" />
                  Recent Activity ({data.ledger?.length || 0})
                </button>

                {showActivity && data.ledger?.length > 0 && (
                  <div className="rounded-2xl bg-black/40 border border-white/10 p-4 sm:p-6">
                    <h3 className="text-white/90 font-normal mb-4 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-white/50" />
                      Recent Activity
                    </h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {data.ledger.slice(0, 50).map((e) => (
                        <div
                          key={e.id}
                          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg bg-black/30 border border-white/10"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-xs font-normal ${e.amount_cents >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {e.amount_cents >= 0 ? '+' : ''}{formatCurrency(e.amount_cents)}
                            </span>
                            <span className="text-white/85 text-xs truncate">{e.description}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-white/40">
                            <span className="truncate max-w-[200px]">{userPrimaryLabel(data.users[e.user_id])}</span>
                            <span>{format(new Date(e.created_at), 'MMM d, h:mm a')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Users & Trips */}
                <div>
                  <h3 className="text-white/90 font-normal mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4 text-white/50" />
                    By User
                  </h3>
                  <div className="space-y-3">
                    {userList.map((userId) => {
                      const trips = byUser[userId] || [];
                      const userInfo = data.users[userId] || {};
                      const balance = data.balances[userId] ?? 0;
                      const allocated = trips
                        .filter((t) => t.status === 'active')
                        .reduce((s, t) => s + (Number(t.allocated_from_reserve_cents) || 0), 0);
                      const isExpanded = expandedUser === userId;

                      return (
                        <div
                          key={userId}
                          className="rounded-2xl bg-black/40 border border-white/10 overflow-hidden"
                        >
                          {/* User row header */}
                          <div className="flex items-center gap-2 px-4 py-3 sm:px-5 sm:py-4 hover:bg-black/30 transition-colors">
                            <button
                              type="button"
                              onClick={() => setExpandedUser(isExpanded ? null : userId)}
                              className="flex items-start gap-2 min-w-0 flex-1 text-left"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" />
                              )}
                              <Mail className="w-4 h-4 text-white/30 flex-shrink-0 mt-0.5" />
                              <div className="min-w-0 flex-1">
                                <p className="text-white/90 text-sm sm:text-base truncate">{userPrimaryLabel(userInfo)}</p>
                                {userShowEmailBelow(userInfo) ? (
                                  <p className="text-white/45 text-xs truncate mt-0.5">{userInfo.email}</p>
                                ) : null}
                              </div>
                              <span className="text-white/40 text-xs flex-shrink-0 self-center">
                                {trips.length} trip{trips.length !== 1 ? 's' : ''}
                              </span>
                            </button>
                            <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                              <div className="text-right">
                                <p className="text-emerald-400 text-sm font-normal">{formatCurrency(balance)}</p>
                                <p className="text-white/30 text-[10px] uppercase">Balance</p>
                              </div>
                              <div className="text-right hidden sm:block">
                                <p className="text-white/85 text-sm">{formatCurrency(allocated)}</p>
                                <p className="text-white/30 text-[10px] uppercase">Allocated</p>
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openCustomerReserveDashboard(userId);
                                }}
                                className="p-1.5 rounded-lg text-white/30 hover:text-emerald-400/90 hover:bg-white/5 transition-colors"
                                title="Open customer Reserve dashboard"
                              >
                                <LayoutDashboard className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  adjustingUser === userId ? cancelAdjust() : openAdjust(userId);
                                }}
                                className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors"
                                title="Adjust balance"
                              >
                                <SlidersHorizontal className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Inline balance adjustment form */}
                          <AnimatePresence>
                            {adjustingUser === userId && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                className="border-t border-white/10 overflow-hidden"
                              >
                                <div className="px-4 py-3 sm:px-5 bg-white/[0.02]">
                                  <p className="text-white/50 text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                    <SlidersHorizontal className="w-3 h-3" />
                                    Adjust Balance
                                  </p>
                                  <div className="flex flex-col sm:flex-row gap-2">
                                    <div className="relative w-full sm:w-32">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        placeholder="0.00"
                                        value={adjustAmount}
                                        onChange={(e) => setAdjustAmount(e.target.value)}
                                        className="pl-6 bg-black/40 border-white/10 text-white placeholder:text-white/20 text-sm h-9"
                                        autoFocus
                                      />
                                    </div>
                                    <Input
                                      type="text"
                                      placeholder="Reason (e.g. Refund processed via Stripe)"
                                      value={adjustDescription}
                                      onChange={(e) => setAdjustDescription(e.target.value)}
                                      className="flex-1 bg-black/40 border-white/10 text-white placeholder:text-white/20 text-sm h-9"
                                      onKeyDown={(e) => e.key === 'Enter' && submitAdjust(userId)}
                                    />
                                    <div className="flex gap-1.5">
                                      <Button
                                        size="sm"
                                        onClick={() => submitAdjust(userId)}
                                        disabled={adjustSubmitting}
                                        className="h-9 px-3 bg-emerald-600 hover:bg-emerald-500 text-white border-0"
                                      >
                                        {adjustSubmitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                        <span className="ml-1">Apply</span>
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={cancelAdjust}
                                        disabled={adjustSubmitting}
                                        className="h-9 px-3 text-white/40 hover:text-white hover:bg-white/5"
                                      >
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                  <p className="text-white/30 text-[11px] mt-2">
                                    Use a negative amount to deduct (e.g. <span className="text-white/50">-48.62</span> to wipe a refunded payment).
                                  </p>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* Expanded trips */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="border-t border-white/10"
                              >
                                <div className="p-4 sm:p-5 space-y-3">
                                  {trips.map((trip) => (
                                    <div
                                      key={trip.id}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => openCustomerReserveDashboard(userId)}
                                      onKeyDown={(e) => e.key === 'Enter' && openCustomerReserveDashboard(userId)}
                                      className="rounded-xl bg-black/30 border border-white/10 p-3 sm:p-4 cursor-pointer hover:bg-white/[0.06] hover:border-white/15 transition-colors text-left w-full"
                                      title="Open customer Reserve dashboard"
                                    >
                                      <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-white/90 font-medium">{formatRoute(trip)}</span>
                                            <span
                                              className={`px-2 py-0.5 rounded-full text-[10px] font-normal ${statusColors[trip.status] || 'bg-white/10 text-white/60'}`}
                                            >
                                              {trip.status}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-3 mt-1.5 text-white/40 text-xs">
                                            <span className="flex items-center gap-1">
                                              <Calendar className="w-3 h-3" />
                                              {formatDate(trip.departure_date)}
                                            </span>
                                            <span>{frequencyLabels[trip.payment_frequency] || trip.payment_frequency}</span>
                                            <span>${fmtCurrency(trip.total_amount_cents || 0)}</span>
                                          </div>
                                        </div>
                                        <div className="text-right flex-shrink-0">
                                          <p className="text-white/85 text-sm">
                                            Paid: {formatCurrency(trip.paid_amount_cents)}
                                          </p>
                                          <p className="text-white/40 text-xs">
                                            Alloc: {formatCurrency(trip.allocated_from_reserve_cents)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
